import { useEffect, useState } from 'react'
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { Icon } from '@iconify/react'
import { useModal } from '@ebay/nice-modal-react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  ProgressBar,
  useContentRefreshDomains,
  useEditProjectTopic,
  useGetProjectDomain,
  useGlobalState,
  useMessage,
  useShowMessage,
} from '@pepper/ui'

import BottomPopup, { BottomPopupBtn } from 'components/BottomPopup'
import SEOTable from 'container/SEOTools/components/SEOTable/SEOTable'
import AddTopicsToProjectModal from '../../Topics/AddTopicsToProjectModal'
import EditProjectTopicModal from '../../Topics/EditProjectTopicModal'
import EditTopicModal from '../../Topics/EditTopicModal'

import { useAddTopics } from 'utils/hooks/useAddTopics'
import { useKeywordLabelling } from 'utils/hooks/IdeaLab/useKeywordLabelling'
import { useTopicSuggestions } from 'utils/hooks/useTopicSuggestions'
import { useGenerateBulkSeoGuidelines } from 'utils/hooks/useGenerateBulkSeoGuidelines'

import { getColumns } from './columns'

import type { SuggestedTopic } from 'types/topic'
import type { EditFormValues } from '../../Topics/EditTopicForm'
import type { Topic } from '@pepper/utils/src/types/seo'

import s from './TopicSuggestionsTable.module.css'

type Props = {
  domainId: string
  companyId: string
  domainRegion: string
  projectId?: string
  themeId?: string
  themeKeywordIds?: string[]
  isDomainRemoved?: boolean
  isContentIdeasPage?: boolean
}

const INTERVAL = 5000
const PAGE_LIMIT = 15
const LOADING_WRAPPER_HEIGHT = 30
const APPLIED_FILTERS_HEIGHT = 48

export const TopicSuggestionsTable = ({
  domainId,
  companyId,
  projectId,
  domainRegion,
  themeId,
  themeKeywordIds,
  isDomainRemoved = false,
  isContentIdeasPage = false,
}: Props) => {
  const history = useHistory()
  const showMessage = useShowMessage()
  const { showError } = useMessage()
  const { permissions } = useGlobalState()
  const { show: showUpsellBanner } = useModal('buy-cmp')
  const { show: showProjectPicker } = useModal('select-project-modal')
  const [activeTopic, setActiveTopic] = useState<SuggestedTopic>()
  const [loadingPercent, setLoadingPercent] = useState<number>(20)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isEditTopicModalOpen, setIsEditTopicModalOpen] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState(projectId)
  const [isSEOGuidelineModalOpen, setIsSEOGuidelineModalOpen] = useState(false)
  const [selectedSuggestionId, setSelectedSuggestionId] = useState('')
  const [queryParams, setQueryParams] = useQueryParams({
    sug_keyword: withDefault(StringParam, undefined),
    sug_topic: withDefault(StringParam, undefined),
    sug_positionLte: withDefault(NumberParam, undefined),
    sug_positionGte: withDefault(NumberParam, undefined),
    sug_page: withDefault(NumberParam, 1),
    sug_limit: withDefault(NumberParam, PAGE_LIMIT),
    sug_exclude_position: withDefault(BooleanParam, false),
    sort: withDefault(StringParam, undefined),
  })

  const hasTopicsPermission = permissions?.topics
  const activeProjectId = selectedProjectId ?? projectId

  const { data: projectDomain } = useGetProjectDomain({
    data: {
      companyId,
      projectId: activeProjectId,
    },
  })

  const { mutateAsync: addTopics } = useAddTopics()
  const { mutateAsync: updateProjectTopic } = useEditProjectTopic()
  const { mutateAsync: generateBulkSeoGuidelines } =
    useGenerateBulkSeoGuidelines()

  const {
    sug_positionGte,
    sug_positionLte,
    sug_keyword,
    sug_topic,
    sug_page,
    sug_limit,
    sug_exclude_position,
  } = queryParams

  const { isGoogleConnected } = useContentRefreshDomains(companyId)

  const { data: topicSuggestions, isLoading } = useTopicSuggestions({
    companyId,
    projectId: activeProjectId,
    domainId,
    themeId,
    themeKeywordIds,
    enabled: !!domainId,
    queryParams: {
      keyword: sug_keyword?.split(','),
      lte: sug_positionLte,
      gte: sug_positionGte,
      page: sug_page,
      limit: sug_limit,
      exists: sug_exclude_position,
      sort: queryParams.sort,
      topic: sug_topic?.split(','),
    },
  })

  const {
    data: keywordLabelling,
    isLoading: isLoadingKeywordLabel,
    error: labellingError,
  } = useKeywordLabelling({
    id: selectedSuggestionId,
    enabled: selectedSuggestionId.length > 0 && !!companyId,
    companyId,
  })

  const handleGenerateBulkSeoGuidelines = async (topics: Topic[]) => {
    const res = await generateBulkSeoGuidelines({
      companyId,
      topics: topics.map(topic => ({
        topic_id: topic.id,
        topic: topic.name,
        region_code: domainRegion,
      })),
    })

    return res
  }

  const handleAddAndUpdateTopic = async (
    _projectId: string,
    values: EditFormValues
  ) => {
    if (!activeTopic) return
    const addTopicResponse = await addTopics({
      companyId,
      projectId: _projectId,
      topicIds: [activeTopic.id],
    })
    const { status, data, error } = addTopicResponse

    if (status === 'success' && data && data.length > 0) {
      const updateTopicResponse = await updateProjectTopic({
        companyId,
        topic: values.name,
        topicId: data[0].id,
        primaryKeyword: values.primaryKeyword,
        secondaryKeywords: values.secondaryKeywords,
      })

      if (updateTopicResponse.status === 'success') {
        if (values.shouldAttachSeoGuidelines) {
          const seoGuidelineResponse = await handleGenerateBulkSeoGuidelines(
            data
          )

          if (seoGuidelineResponse.status === 'error') {
            showError(seoGuidelineResponse.error)
          }
        }
      } else {
        showError(updateTopicResponse.message)
      }
    } else {
      showError(error)
    }
    return addTopicResponse
  }

  const handleAddToProject = (_topic?: SuggestedTopic) => {
    if (!hasTopicsPermission) {
      showUpsellBanner({ id: 'topics' })
      return
    }
    if (!activeProjectId) {
      showProjectPicker({
        activeDomainId: domainId,
        onAfterSelectId: async (_projectId: string) => {
          setSelectedProjectId(_projectId)
          _topic && setSelectedRowKeys([_topic.id])
          setIsSEOGuidelineModalOpen(true)
        },
      })
    } else {
      _topic && setSelectedRowKeys([_topic.id])
      setIsSEOGuidelineModalOpen(true)
    }
    setSelectedProjectId(undefined)
  }

  const handleEditSubmit = (values: {
    name: string
    primaryKeyword?: string
    secondaryKeywords?: string[]
    shouldAttachSeoGuidelines?: boolean
  }) => {
    setIsEditTopicModalOpen(false)
    showProjectPicker({
      activeDomainId: domainId,
      onAfterSelectId: async (_projectId: string) => {
        const response = await handleAddAndUpdateTopic(_projectId, values)
        if (response?.status === 'success') {
          showMessage({
            type: 'success',
            message: (
              <div className={s.toastMessage}>
                Topic added successfully
                <Button
                  type="default"
                  onClick={() => history.push(`/project/${_projectId}/topics`)}
                >
                  Go to project
                </Button>
              </div>
            ),
          })
        } else {
          showError('Something went wrong while adding topic to the project')
        }
        return response
      },
    })
  }

  const handleEditSuggestedTopic = (_topic: SuggestedTopic) => {
    if (!hasTopicsPermission) {
      showUpsellBanner({ id: 'topics' })
      return
    }
    setActiveTopic(_topic)
    if (!activeProjectId) {
      setIsEditTopicModalOpen(true)
    } else {
      setIsEditModalOpen(true)
    }
    setSelectedProjectId(undefined)
  }

  const suggestionIds = (topicSuggestions?.records ?? []).map(
    record => record.id
  )

  const filteredSelectedRows = selectedRowKeys.filter(rowKey => {
    return suggestionIds.includes(`${rowKey}`)
  })

  const getRowKey = (row: SuggestedTopic) => {
    return row.id
  }

  if (labellingError) {
    showError(labellingError)
  }

  const filterHeight =
    !!sug_topic || !!sug_keyword || sug_exclude_position
      ? APPLIED_FILTERS_HEIGHT
      : 0

  const getTableContentHeight = (jobStatus: boolean) => {
    const loadingWrapperHeight = jobStatus ? LOADING_WRAPPER_HEIGHT : 0

    if (!isContentIdeasPage) {
      return themeId
        ? `calc(100vh - ${234 + loadingWrapperHeight + filterHeight}px)`
        : `calc(100vh - ${210 + loadingWrapperHeight}px)`
    }
    return `calc(100vh - ${305 + loadingWrapperHeight + filterHeight}px)`
  }

  const getTableHeight = (jobStatus: boolean) => {
    const loadingWrapperHeight = jobStatus ? LOADING_WRAPPER_HEIGHT : 0

    if (!isContentIdeasPage) {
      return themeId
        ? `calc(100vh - ${130 + loadingWrapperHeight + filterHeight}px)`
        : `calc(100vh - ${110 + loadingWrapperHeight}px)`
    }
    return `calc(100vh - ${200 + loadingWrapperHeight + filterHeight}px)`
  }

  useEffect(() => {
    const interval = setInterval(
      () =>
        setLoadingPercent(prev => {
          if (prev < 90) return prev + 10
          return prev
        }),
      INTERVAL
    )

    return () => clearInterval(interval)
  }, [])

  return (
    <div className={s.table}>
      {!!topicSuggestions?.job_status && !isLoading && (
        <div className={s.loadingWrapper}>
          <div className={s.loaderText}>Fetching more topics…</div>
          <ProgressBar
            size="small"
            strokeColor="#5917b5"
            status="active"
            percent={loadingPercent}
            className={s.loader}
            strokeLinecap="square"
          />
        </div>
      )}
      <div className={s.tableWrapper}>
        <SEOTable<SuggestedTopic>
          bordered={false}
          rowKey={getRowKey}
          rowClassName={s.row}
          loading={isLoading}
          dataSource={topicSuggestions?.records}
          columns={getColumns({
            handleAddToProject,
            disableCta: filteredSelectedRows.length > 0,
            onEditTopic: handleEditSuggestedTopic,
            isLoadingKeywordLabel,
            onGetKeywordLabel: setSelectedSuggestionId,
            pageRankingResults: keywordLabelling,
            domain: projectDomain?.domain.domain_url,
            isGoogleConnected,
            companyId,
            isDomainRemoved,
            isContentIdeasPage,
          })}
          size="small"
          pagination={{
            onChange: pageNumber =>
              setQueryParams({
                sug_page: pageNumber,
              }),
            total: topicSuggestions?.total_records,
            current: sug_page,
            pageSize: sug_limit,
            showSizeChanger:
              (topicSuggestions?.total_records ?? 0) > PAGE_LIMIT,
            pageSizeOptions: ['15', '30', '50', '100'],
            onShowSizeChange(current, size) {
              setQueryParams({
                sug_page: current,
                sug_limit: size,
              })
            },
          }}
          style={{
            height: getTableHeight(!!topicSuggestions?.job_status),
          }}
          rowSelection={{
            onChange: setSelectedRowKeys,
            selectedRowKeys: filteredSelectedRows,
          }}
          scroll={{
            y: getTableContentHeight(!!topicSuggestions?.job_status),
            scrollToFirstRowOnChange: true,
          }}
        />
      </div>
      <BottomPopup
        align={isContentIdeasPage ? 'center' : 'right'}
        selectedCount={filteredSelectedRows.length}
        onClose={() => setSelectedRowKeys([])}
      >
        <BottomPopupBtn
          icon={<Icon icon="lucide:plus" width={14} />}
          onClick={() => handleAddToProject()}
        >
          Add to Project
        </BottomPopupBtn>
      </BottomPopup>

      {!!topicSuggestions?.records?.length && !!activeProjectId && (
        <AddTopicsToProjectModal
          open={isSEOGuidelineModalOpen}
          onClose={() => {
            setIsSEOGuidelineModalOpen(false)
            setSelectedRowKeys([])
          }}
          onSuccess={() => setSelectedRowKeys([])}
          selectedTopics={topicSuggestions.records.filter(record =>
            filteredSelectedRows.includes(record.id)
          )}
          companyId={companyId}
          projectId={activeProjectId}
          domainRegion={domainRegion}
        />
      )}

      {!!activeProjectId && (
        <EditTopicModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setActiveTopic(undefined)
          }}
          domainRegion={domainRegion}
          onSuccess={() => setSelectedRowKeys([])}
          companyId={companyId}
          projectId={activeProjectId}
          selectedSuggestedTopic={activeTopic}
        />
      )}

      <EditProjectTopicModal
        open={isEditTopicModalOpen}
        selectedTopic={activeTopic}
        onSubmit={handleEditSubmit}
        onClose={() => {
          setIsEditTopicModalOpen(false)
          setActiveTopic(undefined)
        }}
      />
    </div>
  )
}
